import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { apiService, extractApiErrorMessage } from '../services/apiService';
import { useProjects } from '../context/ProjectContext';
import { AgentInlineSelect } from './ai-agent-ui';
import type { AgentMessageResponse } from '../types/agent';
import { isAgentFailureMessage, isAgentSuccessMessage } from '../utils/agentChatMessageStyle';
import { ensureAgentChatForProject } from '../utils/agentChatBootstrap';
import { validateAgentProjectName } from '../utils/agentProjectUtils';

export type AgentWidgetCreateSelectProjectFormProps = {
  chatId?: number | null;
  initialSelectionMode?: 'new' | 'existing';
  onClose: () => void;
  onUserMessage: (text: string) => void;
  onAgentReply: (res: AgentMessageResponse) => void | Promise<void>;
  onChatIdChange?: (chatId: number) => void;
  onLoadingChange?: (loading: boolean) => void;
  onProjectResolved?: (project: { id: number; name: string }) => void;
};

const AgentWidgetCreateSelectProjectForm: React.FC<AgentWidgetCreateSelectProjectFormProps> = ({
  chatId,
  initialSelectionMode = 'new',
  onClose,
  onUserMessage,
  onAgentReply,
  onChatIdChange,
  onLoadingChange,
  onProjectResolved,
}) => {
  const { refreshProjects, setSelectedProject } = useProjects();
  const [selectionMode, setSelectionMode] = useState<'new' | 'existing'>(initialSelectionMode);
  const [projectName, setProjectName] = useState('');
  const [projectNameError, setProjectNameError] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');
  const [availableProjects, setAvailableProjects] = useState<Array<{ id: number; name: string }>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const projects = await apiService.getProjects();
        if (!cancelled) {
          setAvailableProjects(projects.map((p) => ({ id: p.id, name: p.name })));
        }
      } catch {
        if (!cancelled) setAvailableProjects([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const runAgentAction = async (
    action: string,
    data: Record<string, unknown>,
    userText: string,
    chatIdOverride: number,
  ) => {
    onUserMessage(userText);
    setSubmitting(true);
    onLoadingChange?.(true);
    try {
      const res = await apiService.agentMessage({
        action,
        chatId: chatIdOverride,
        payload: data,
      });
      onChatIdChange?.(chatIdOverride);
      const projectId =
        res.state?.projectId ??
        (typeof data.projectId === 'number' ? data.projectId : undefined);
      if (projectId != null) {
        await refreshProjects();
        try {
          const list = await apiService.getProjects();
          const match = list.find((p) => p.id === projectId);
          if (match) {
            setSelectedProject(match);
            onProjectResolved?.({ id: match.id, name: match.name });
          } else if (typeof data.projectName === 'string' && data.projectName.trim()) {
            onProjectResolved?.({ id: projectId, name: data.projectName.trim() });
          } else if (res.state?.projectName) {
            onProjectResolved?.({ id: projectId, name: res.state.projectName });
          }
        } catch {
          if (res.state?.projectName) {
            onProjectResolved?.({ id: projectId, name: res.state.projectName });
          }
        }
      }
      await onAgentReply(res);
      if (!res.error && !isAgentFailureMessage(res.reply || '', res.actions)) {
        onClose();
      }
    } finally {
      setSubmitting(false);
      onLoadingChange?.(false);
    }
  };

  const handleSubmit = async () => {
    if (selectionMode === 'new') {
      const name = projectName.trim().replace(/\s+/g, ' ');
      if (!name) return;
      const nameError = validateAgentProjectName(name);
      setProjectNameError(nameError);
      if (nameError) return;
      const userText = `Create project "${name}"`;
      onUserMessage(userText);
      setSubmitting(true);
      onLoadingChange?.(true);
      setFormError(null);
      try {
        const res = await apiService.agentMessage({
          action: 'create_project_only',
          chatId: chatId ?? undefined,
          message: userText,
          payload: { projectName: name },
        });
        if (res.state?.chatId != null) {
          onChatIdChange?.(res.state.chatId as number);
          if (res.state.projectName && !res.error && !isAgentFailureMessage(res.reply || '', res.actions)) {
            try {
              await apiService.agentRenameChat(res.state.chatId, res.state.projectName);
            } catch {
              /* best-effort */
            }
          }
        }
        if (
          res.state?.projectId != null &&
          !res.error &&
          !isAgentFailureMessage(res.reply || '', res.actions)
        ) {
          await refreshProjects();
          try {
            const list = await apiService.getProjects();
            const match = list.find((p) => p.id === res.state!.projectId);
            if (match) {
              setSelectedProject(match);
              onProjectResolved?.({ id: match.id, name: match.name });
            }
          } catch {
            /* ignore */
          }
        }
        await onAgentReply(res);
        if (!res.error && !isAgentFailureMessage(res.reply || '', res.actions)) {
          onClose();
        }
      } catch (err: unknown) {
        setFormError(extractApiErrorMessage(err) || 'Failed to create project');
      } finally {
        setSubmitting(false);
        onLoadingChange?.(false);
      }
      return;
    }
    if (!selectedProjectId) return;
    const selected = availableProjects.find((p) => p.id === selectedProjectId);
    if (!selected) return;
    const { chatId: targetChatId } = await ensureAgentChatForProject({
      id: selected.id,
      name: selected.name,
    }, undefined, { forceListed: true });
    await runAgentAction(
      'select_project',
      { projectId: selectedProjectId },
      `Select project "${selected.name}"`,
      targetChatId,
    );
  };

  const busy = submitting;

  return (
    <Box
      sx={{
        mt: 1,
        mb: 0.5,
        p: 1.25,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.95)',
        border: '1px solid rgba(15,23,42,0.12)',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        Create/Select Project
      </Typography>

      <Box sx={{ display: 'flex', gap: 0.75, mb: 1 }}>
        <Button
          size="small"
          variant={selectionMode === 'new' ? 'contained' : 'outlined'}
          onClick={() => {
            setSelectionMode('new');
            setSelectedProjectId('');
          }}
          disabled={busy}
          sx={{ textTransform: 'none', flex: 1 }}
        >
          Create New
        </Button>
        <Button
          size="small"
          variant={selectionMode === 'existing' ? 'contained' : 'outlined'}
          onClick={() => setSelectionMode('existing')}
          disabled={busy}
          sx={{ textTransform: 'none', flex: 1 }}
        >
          Select Existing
        </Button>
      </Box>
      {selectionMode === 'new' ? (
        <TextField
          label="Project Name"
          size="small"
          fullWidth
          value={projectName}
          onChange={(e) => {
            const next = e.target.value;
            setProjectName(next);
            setProjectNameError(validateAgentProjectName(next));
          }}
          onBlur={() => setProjectNameError(validateAgentProjectName(projectName))}
          error={!!projectNameError}
          helperText={projectNameError || undefined}
          disabled={busy}
          sx={{ mb: 1 }}
        />
      ) : (
        <Box sx={{ mb: 1 }}>
          <AgentInlineSelect
            id="widget-agent-project"
            label="Project"
            fullWidth
            value={selectedProjectId === '' ? '' : selectedProjectId}
            disabled={busy}
            emptyLabel="No projects found"
            options={availableProjects.map((project) => ({
              value: project.id,
              label: project.name,
            }))}
            onChange={(raw) => {
              const parsed = Number(raw);
              setSelectedProjectId(Number.isNaN(parsed) || raw === '' ? '' : parsed);
            }}
          />
        </Box>
      )}
      {selectionMode === 'existing' && availableProjects.length === 0 && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
          No existing projects found. Choose Create New.
        </Typography>
      )}
      {formError && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
          {formError}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={
            busy ||
            (selectionMode === 'new'
              ? !projectName.trim() ||
                !!projectNameError ||
                !!validateAgentProjectName(projectName)
              : !selectedProjectId)
          }
          sx={{ textTransform: 'none' }}
        >
          {busy ? (
            <CircularProgress size={16} color="inherit" />
          ) : selectionMode === 'new' ? (
            'Create'
          ) : (
            'Select'
          )}
        </Button>
      </Box>

      <Divider sx={{ mt: 1.25 }} />
    </Box>
  );
};

export function mapAgentReplyToWidgetMessage(res: AgentMessageResponse) {
  const reply = res.reply?.trim() || 'Done.';
  const isFailure = Boolean(res.error) || isAgentFailureMessage(reply, res.actions);
  return {
    text: reply,
    agentActions: res.actions,
    agentFailure: isFailure,
    agentSuccess: !isFailure && isAgentSuccessMessage(reply, res.actions),
  };
}

export default AgentWidgetCreateSelectProjectForm;
